import { AfterViewInit, Directive, ElementRef, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { fromEvent } from 'rxjs/internal/observable/fromEvent';
import { debounceTime, filter, map, pairwise, tap } from 'rxjs/operators';
import { MatAutocomplete } from '@angular/material/autocomplete';

@Directive({
  selector: '[fbAutocompleteScroll]',
})
export class AutocompleteScrollDirective extends OnDestroyMixin implements OnInit, AfterViewInit {
  @Input() threshold: string = '64';
  @Input() endOfData: boolean;
  @Output() scrollEvent: EventEmitter<void> = new EventEmitter();

  get selectItemHeightPx(): number {
    return parseFloat(getComputedStyle(this.panel).fontSize) * 3;
  }

  get panel(): HTMLElement {
    return this.autocomplete?.panel?.nativeElement;
  }

  private thrPx: number = 0;
  private thrPc: number = 0;

  constructor(private element: ElementRef, private autocomplete: MatAutocomplete) {
    super();
  }

  ngOnInit(): void {
    this.evaluateThreshold();
  }

  ngAfterViewInit(): void {
    this.autocomplete.opened.pipe(untilComponentDestroyed(this), debounceTime(1)).subscribe(() => {
      this.registerScrollListener();
    });
  }

  evaluateThreshold(): void {
    if (this.threshold.lastIndexOf('%') > -1) {
      this.thrPx = 0;
      this.thrPc = parseFloat(this.threshold) / 100;
    } else {
      this.thrPx = parseFloat(this.threshold);
      this.thrPc = 0;
    }
  }

  registerScrollListener(): void {
    const delayBeforeNextLoad: number = 100;

    fromEvent(this.panel, 'scroll')
      .pipe(
        untilComponentDestroyed(this),
        map((event: Event) => (event?.target as Element)?.scrollTop),
        pairwise<number>(),
        filter(([scrollTop1, scrollTop2]: [number, number]) => scrollTop1 < scrollTop2),
        debounceTime(delayBeforeNextLoad),
        filter(() => Boolean(this.panel)),
        tap(([, scrollTop]: [number, number]) => {
          this.handleScrollEvent(scrollTop);
        }),
      )
      .subscribe();
  }

  handleScrollEvent(scrollTop: number): void {
    if (this.endOfData) {
      return;
    }
    const singleOptionHeight: number = this.selectItemHeightPx;
    const countOfRenderedOptions: number = this.autocomplete.options.length;
    const infiniteScrollDistance: number = singleOptionHeight * countOfRenderedOptions;
    const threshold: number = this.thrPc !== 0 ? infiniteScrollDistance * this.thrPc : this.thrPx;

    const scrolledDistance: number = +this.panel.clientHeight + scrollTop;

    if (scrolledDistance + threshold >= infiniteScrollDistance) {
      this.scrollEvent.emit();
    }
  }
}
